import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'

import { Button } from '@/ui/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/components/common/Dialog'

interface ConfirmNoSourceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  isSubmitting?: boolean
}

/**
 * Asks for an explicit confirmation before recording a fact without a source
 * URL. Shown on creation only — an existing fact can be edited freely.
 */
export function ConfirmNoSourceDialog({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting = false,
}: ConfirmNoSourceDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
            {t('lead_detail.fact_no_source_title')}
          </DialogTitle>
          <DialogDescription>{t('lead_detail.fact_no_source_message')}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="pt-2">
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
          >
            {t('lead_detail.fact_no_source_cancel')}
          </Button>
          <Button type="button" isLoading={isSubmitting} onClick={onConfirm}>
            {t('lead_detail.fact_no_source_confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
